import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { validatePasswordStrength } from '../../common/utility/password-validation.util';
import { UserService } from '../../feature/settings/users/service/user.service';
import { AuthService } from '../../service/auth.service';

export type ChangePasswordMode = 'self' | 'admin';

@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    PasswordModule,
    ButtonModule,
  ],
  templateUrl: './change-password-dialog.component.html',
})
export class ChangePasswordDialogComponent {
  /**
   * 'self' = user changing own password (shows current-password field).
   * 'admin' = admin resetting another user's password (no current-password field).
   */
  @Input() mode: ChangePasswordMode = 'self';

  /** Required when mode = 'admin'. Can be overridden via open(). */
  @Input() userId: string | null = null;

  /** Optional display name shown in header (admin mode). */
  @Input() userName: string | null = null;

  /** Emits when password change succeeds. */
  @Output() changed = new EventEmitter<void>();

  visible = signal(false);
  loading = signal(false);
  errorMessage = signal<string | null>(null);

  /** Form fields as signals so `rules()` recomputes live as the user types. */
  form = {
    oldPassword: signal(''),
    newPassword: signal(''),
    confirmPassword: signal(''),
  };

  private _userService = inject(UserService);
  private _messageService = inject(MessageService);
  private _authService = inject(AuthService);

  /** Live password-rule checklist. Recomputes whenever form.newPassword changes. */
  rules = computed(() => {
    const pwd = this.form.newPassword();
    return {
      length: pwd.length >= 8,
      lower: /[a-z]/.test(pwd),
      upper: /[A-Z]/.test(pwd),
      digit: /\d/.test(pwd),
      special: /[~!@#$%&*_\-+=]/.test(pwd),
    };
  });

  get dialogHeader(): string {
    if (this.mode === 'admin') {
      return this.userName
        ? `Change Password — ${this.userName}`
        : 'Change Password';
    }
    return 'Change Password';
  }

  /** Public API: open dialog programmatically. */
  open(userId?: string, userName?: string): void {
    if (this.mode === 'admin') {
      if (userId !== undefined) this.userId = userId;
      if (userName !== undefined) this.userName = userName;
    }
    this.resetForm();
    this.visible.set(true);
  }

  /** Public API: close dialog (no-op while loading). */
  close(): void {
    if (this.loading()) return;
    this.visible.set(false);
    this.resetForm();
  }

  onDialogHide(): void {
    this.resetForm();
  }

  private resetForm(): void {
    this.form.oldPassword.set('');
    this.form.newPassword.set('');
    this.form.confirmPassword.set('');
    this.errorMessage.set(null);
  }

  submit(): void {
    this.errorMessage.set(null);

    const oldPassword = this.form.oldPassword();
    const newPassword = this.form.newPassword();
    const confirmPassword = this.form.confirmPassword();

    if (this.mode === 'self' && !oldPassword) {
      this.errorMessage.set('Please enter your current password.');
      return;
    }

    const strength = validatePasswordStrength(newPassword);
    if (!strength.valid) {
      this.errorMessage.set(strength.errors.join(' '));
      return;
    }

    if (newPassword !== confirmPassword) {
      this.errorMessage.set('Passwords do not match.');
      return;
    }

    if (this.mode === 'self' && newPassword === oldPassword) {
      this.errorMessage.set(
        'New password must be different from the current password.',
      );
      return;
    }

    if (this.mode === 'admin' && !this.userId) {
      this.errorMessage.set('Missing user id.');
      return;
    }

    this.loading.set(true);

    // Both branches return Observable<void> → subscribe resolves to one signature.
    const request$: Observable<void> =
      this.mode === 'self'
        ? this._userService.changeMyPassword(
            oldPassword,
            newPassword,
            confirmPassword,
          )
        : this._userService.updatePassword(this.userId!, newPassword);

    request$.subscribe({
      next: () => {
        this.loading.set(false);
        if (this.mode === 'admin') {
          this._messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password changed successfully.',
          });
          this.visible.set(false);
          this.resetForm();
          this.changed.emit();
          return;
        }

        this._messageService.add({
          severity: 'success',
          summary: 'Password Changed',
          detail:
            'Your password was changed. Please sign in again with your new password.',
          life: 2500,
        });

        this.visible.set(false);
        this.resetForm();
        this.changed.emit();

        setTimeout(() => {
          this._authService.logout();
        }, 3000);
      },
      error: (err: any) => {
        this.loading.set(false);
        this.errorMessage.set(
          err?.error?.message ||
            'Failed to change password. Please try again.',
        );
      },
    });
  }
}